import { PushData, PushType } from '../types/push-data';

/**
 * Перетворює `pushData` в URL для роутингу з notification на сутність.
 * Повертає `null` якщо тип не має дефолтного маршруту (на цій платформі).
 *
 * Для типів, які залежать від ролі (наприклад, APPOINTMENT — клієнт/експерт мають різні
 * сторінки), використовується `sidebarMode` з `localStorage('sidebar-mode')`. Це той самий
 * `X-Panel` контекст, що йде на бек, і він автентичний відповідно до того, що відкрив юзер.
 *
 * Чому фронт-таблиця, а не бек-URL: бек універсальний для web/mobile/...; кожна платформа
 * має свої маршрути. Додавати нові типи — додавати case тут (і дзеркально у мобільному).
 *
 * Контракт `pushData` синхронізований з `api/src/modules/push/push-data.types.ts`.
 */

export type SidebarMode = 'client' | 'expert' | 'admin' | 'developer' | 'general' | null;

function readSidebarMode(): SidebarMode {
  const raw = typeof window !== 'undefined' ? localStorage.getItem('sidebar-mode') : null;
  if (raw === 'client' || raw === 'expert' || raw === 'admin' || raw === 'developer' || raw === 'general') {
    return raw;
  }
  return null;
}

export function resolvePushTarget(pushData: PushData | null | undefined, mode?: SidebarMode): string | null {
  if (!pushData) return null;
  const sidebarMode = mode ?? readSidebarMode();

  switch (pushData.type) {
    case PushType.APPOINTMENT:
    case PushType.APPOINTMENT_REVIEW: {
      const base = sidebarMode === 'expert' ? '/expert/appointments' : '/appointments';
      return `${base}?id=${pushData.id}`;
    }
    case PushType.CHAT_MESSAGE:
      return `/chat/${pushData.conversationId}`;
    case PushType.NEWS:
      return `/news/${pushData.id}`;
    case PushType.CALENDAR_EVENT:
      // Календар відкривається на dashboard у клієнтській панелі. Конкретного deep-link
      // на одну подію поки нема — у разі потреби розширити.
      return sidebarMode === 'expert' ? null : '/dashboard';
    case PushType.CONNECTION_REQUEST:
      // Запит на підключення приходить експерту → список клієнтів.
      return '/expert/clients';
    case PushType.CONNECTION_RESPONSE:
      // Відповідь на запит приходить клієнту → список експертів.
      return '/clients/experts';
    case PushType.PERMISSIONS_UPDATED:
      return '/settings/permissions';
    case PushType.PAYMENT_SUCCESS:
    case PushType.PAYMENT_FAILED:
      return '/settings/billing';
    case PushType.SURVEY_INVITE_ACCEPTED:
    case PushType.SURVEY_INVITE_COMPLETED:
      // Сповіщення про інвайт на опитування приходить лише запросившому експерту.
      return `/client/${pushData.clientId}/questionnaires/${pushData.templateId}`;
    case PushType.SURVEY_ASSIGNED:
      // Сповіщення про призначене опитування приходить клієнту, якому призначили.
      return `/questionnaires/${pushData.templateId}`;
    case PushType.ORDER_UPDATED:
    case PushType.DEFAULT:
      return null;
    default: {
      // Exhaustiveness check: якщо додано новий PushType — TS змусить розширити switch.
      const _exhaustive: never = pushData;
      void _exhaustive;
      return null;
    }
  }
}
